// =============================================
// firebase/avatarFrames.ts — v8 namespaced API 準拠
// 依存: あなたのプロジェクトの /src/firebase.ts から
//   export const db, storage, timestamp, nowTimestamp
// をインポートして使います。firebase 初期化はユーザー提示コードに準拠。
// =============================================

import firebase from 'firebase/app';
import { db, storage, timestamp, nowTimestamp } from '../../firebase';

import type {
  AvatarFrameGroup,
  AvatarFrameGroupCreate,
  AvatarFrameGroupUpdate,
  FrameEntry,
} from '../../types/avatarFrame';

// ===== 型（必要なら共通 types へ移動） =====
// export type Rank = 'bronze' | 'silver' | 'gold' | 'platinum' | 'admin';

// export type FrameEntry = {
//   id: string; // グループをまたがってユニーク
//   label?: string;
//   order: number;
//   enabled: boolean;
//   uri?: string;
//   v?: string; // 画像更新時のバージョン/ハッシュ
//   pxWidth?: number;
//   pxHeight?: number;
//   // 運用都合で保存したい場合のみ: 画像の storage パス
//   storagePath?: string;
// };

// export interface AvatarFrameGroup {
//   id: string; // groupId（例: "starter", "neko", "event23"）
//   name: string;
//   description?: string;
//   enabled: boolean; // 配布ON/OFF
//   order: number; // 並び順

//   // ランク制御
//   minRank?: Rank;
//   allowedRanks?: Rank[];

//   // 付与グラント条件
//   requiredGrants?: string[];

//   // 配布期間（任意）
//   startAt?: firebase.firestore.Timestamp;
//   endAt?: firebase.firestore.Timestamp;

//   // 含まれるフレーム
//   frames: FrameEntry[];
//   lastRefreshAt: number; // 運用管理用
// }

const CL = 'AvatarFrame';

// =====================================================
// Firestore アクセサ
// =====================================================

export const listAvatarFrameGroups = async (): Promise<AvatarFrameGroup[]> => {
  const snap = await db.collection(CL).orderBy('order', 'asc').get();
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
};

export const getAvatarFrameGroup = async (
  groupId: string
): Promise<AvatarFrameGroup | null> => {
  const doc = await db.collection(CL).doc(groupId).get();
  return doc.exists
    ? ({ id: doc.id, ...(doc.data() as any) } as AvatarFrameGroup)
    : null;
};

export const createAvatarFrameGroup = async (
  group: AvatarFrameGroup
): Promise<boolean> => {
  await db
    .collection(CL)
    .doc(group.id)
    .set({
      ...group,
      publicOpen: group.publicOpen ?? false, // ★ 既定false
      lastRefreshAt: Date.now(),
    });
  return true;
};

export const updateAvatarFrameGroup = async (
  groupId: string,
  patch: Partial<AvatarFrameGroup>
): Promise<boolean> => {
  await db
    .collection(CL)
    .doc(groupId)
    .set(
      {
        ...patch,
        publicOpen: patch.publicOpen ?? false, // 任意 → 既定 false
        lastRefreshAt: Date.now(),
      },
      { merge: true }
    );
  return true;
};

// frames[] 内の単一要素を upsert（存在すれば置換、なければ追加）
export const upsertFrameEntry = async (
  groupId: string,
  frameId: string,
  updater: (prev: FrameEntry | undefined) => FrameEntry
): Promise<void> => {
  const doc = await db.collection(CL).doc(groupId).get();
  if (!doc.exists) throw new Error('Group not found');
  const data = doc.data() as AvatarFrameGroup;
  const frames = data.frames || [];
  const idx = frames.findIndex((f) => f.id === frameId);
  const nextEntry = updater(idx >= 0 ? frames[idx] : undefined);
  const nextFrames = [...frames];
  if (idx >= 0) nextFrames[idx] = nextEntry;
  else nextFrames.push(nextEntry);
  await db
    .collection(CL)
    .doc(groupId)
    .set({ frames: nextFrames, lastRefreshAt: Date.now() }, { merge: true });
};

// フレーム ID グローバル一意チェック（クライアント簡易版）
export const isFrameIdUnique = async (frameId: string): Promise<boolean> => {
  const groups = await listAvatarFrameGroups();
  return groups.every((g) => !(g.frames || []).some((f) => f.id === frameId));
};

// 自分のグループを無視して Frame ID の重複を検出
export const isFrameIdUniqueExcept = async (
  frameId: string,
  ignoreGroupId: string
): Promise<boolean> => {
  const groups = await listAvatarFrameGroups();
  return groups.every(
    (g) =>
      g.id === ignoreGroupId || !(g.frames || []).some((f) => f.id === frameId)
  );
};

// =====================================================
// Storage（画像アップロード/削除/差し替え）
// 保存先: avatarFrame/${groupId}/<filename>
// ※ 仕様に合わせ frameId サブフォルダは作らず groupId 直下に格納
// =====================================================

const storagePathFor = (groupId: string, fileName: string) =>
  `avatarFrame/${groupId}/${fileName}`;

export const uploadFrameImage = async (
  groupId: string,
  file: File,
  fileName?: string
): Promise<{ uri: string; v: string; path: string }> => {
  const name = fileName || `${Date.now()}_${file.name}`;
  const path = storagePathFor(groupId, name);
  const ref = storage.ref().child(path);
  const res = await ref.put(file);
  const uri = await res.ref.getDownloadURL();
  const v = String(res.metadata.generation || Date.now());
  return { uri, v, path };
};

export const deleteFrameImageByPath = async (path: string): Promise<void> => {
  await storage.ref().child(path).delete();
};

// 差し替え（新規アップロード → 旧ファイル（指定時）削除）
export const replaceFrameImage = async (
  groupId: string,
  newFile: File,
  oldPath?: string | null
): Promise<{ uri: string; v: string; path: string }> => {
  const up = await uploadFrameImage(groupId, newFile);
  if (oldPath) {
    try {
      await deleteFrameImageByPath(oldPath);
    } catch (e) {
      /* ignore */
    }
  }
  return up;
};

// =====================================================
// 作成ヘルパ: 空グループ
// =====================================================
export const emptyGroup = (): AvatarFrameGroup => ({
  id: '',
  name: '',
  description: '',
  enabled: true,
  order: 0,
  frames: [],
  lastRefreshAt: Date.now(),
});

// =====================================================
// frames[] ヘルパ
// =====================================================
export const replaceFrameEntry = (
  frames: FrameEntry[],
  frameId: string,
  patch: Partial<FrameEntry>
): FrameEntry[] => {
  return frames.map((f) => (f.id === frameId ? { ...f, ...patch } : f));
};

// =====================================================
// 画面側 利用サンプル
// =====================================================
/*
// 新規作成
const g = emptyGroup();
g.id = 'neko';
g.name = 'ねこフレーム';
await createAvatarFrameGroup(g);

// 画像アップロード
const { uri, v, path } = await uploadFrameImage(g.id, file);
await upsertFrameEntry(g.id, 'f601', (prev) => ({
  id: 'f601',
  label: 'Neko-1',
  order: 0,
  enabled: true,
  uri,
  v,
  storagePath: path,
}));

// 一覧
const list = await listAvatarFrameGroups();

// 編集保存（メタのみ）
await updateAvatarFrameGroup(g.id, { name: 'ねこフレーム（改）', enabled: false });
*/
